/**
 * File type utilities and helpers
 */
import { FileTypes, KnownMimeType } from '@/lib/types/FilesT'
import { FILE_TYPE_CATEGORIES, FILE_TYPES, MIME_ICON_MAP } from '@/constants/files'

/**
 * Get file type category from MIME type
 */
export const getFileTypeCategory = (mimeType: KnownMimeType | string | null): string => {
  if (!mimeType) return FILE_TYPE_CATEGORIES.OTHER

  // 🔥 grouped checks first (fast + scalable)
  if (mimeType.startsWith('image/')) return FILE_TYPE_CATEGORIES.IMAGE
  if (mimeType.startsWith('video/') || mimeType === 'application/mp4')
    return FILE_TYPE_CATEGORIES.VIDEO
  if (mimeType.startsWith('audio/')) return FILE_TYPE_CATEGORIES.AUDIO

  // 🔍 specific types
  if (mimeType === 'application/pdf') return FILE_TYPE_CATEGORIES.PDF

  if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return FILE_TYPE_CATEGORIES.WORD
  }

  if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return FILE_TYPE_CATEGORIES.EXCEL
  }

  if (
    mimeType === 'application/zip' ||
    mimeType === 'application/x-rar-compressed' ||
    mimeType === 'application/x-7z-compressed' ||
    mimeType === 'application/x-tar' ||
    mimeType === 'application/gzip'
  ) {
    return FILE_TYPE_CATEGORIES.ARCHIVE
  }

  if (
    mimeType === 'application/vnd.ms-powerpoint' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mimeType === 'application/x-cfb'
  ) {
    return FILE_TYPE_CATEGORIES.PPT
  }

  if (mimeType === 'text/plain') return FILE_TYPE_CATEGORIES.TEXT

  return FILE_TYPE_CATEGORIES.OTHER
}

/**
 * Format file size to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Check if file is image
 */
export const isImageFile = (mimeType: string): boolean => {
  return mimeType.startsWith('image/')
}

/**
 * Fetch a remote image URL (via the /api/fetch-remote-image proxy, to avoid CORS)
 * and turn it into a File so it can be attached to a multipart form upload.
 */
export const fetchRemoteImageAsFile = async (url: string): Promise<File | null> => {
  try {
    // blob:/data: URLs are already local to this browser tab — the server-side
    // proxy can't fetch them, so fetch them directly instead of proxying.
    const isLocalUrl = url.startsWith('blob:') || url.startsWith('data:')
    const response = await fetch(
      isLocalUrl ? url : `/api/fetch-remote-image?url=${encodeURIComponent(url)}`
    )
    if (!response.ok) return null

    const blob = await response.blob()
    const filename = response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1]

    return new File([blob], filename || 'avatar', { type: blob.type })
  } catch (error) {
    console.error('Error fetching remote image:', error)
    return null
  }
}

export const getFileIcon = (
  fileType?: KnownMimeType | string | null,
  type: FileTypes | string | null = FILE_TYPES.FILE
) => {
  if (type === FILE_TYPES.FOLDER) return 'folder'
  if (!fileType) return 'file'

  // Group-based checks (fast path)
  if (fileType.startsWith('image/')) return 'image'
  if (fileType.startsWith('video/')) return 'video-file'
  if (fileType.startsWith('audio/')) return 'audio'

  // Exact match
  if (MIME_ICON_MAP[fileType]) {
    return MIME_ICON_MAP[fileType]
  }

  return 'file'
}
